export const MAX_CANVAS_SIDE = 4096
export const MAX_CANVAS_PIXELS = 12_000_000
export const MAX_UNDO_COMMANDS = 100
export const MAX_COMMANDS = 500
export const MAX_TOTAL_POINTS = 50_000
export const MAX_POINTS_PER_STROKE = 2_000

export type PaintTool = 'brush' | 'eraser' | 'line' | 'rectangle' | 'ellipse' | 'arrow' | 'text'
export type PaintExportFormat = 'image/png' | 'image/jpeg' | 'image/webp'

export interface PaintPoint {
  x: number
  y: number
}

interface PaintStyle {
  color: string
  width: number
}

export type PaintCommand =
  | ({ kind: 'stroke'; points: readonly PaintPoint[]; erase: boolean } & PaintStyle)
  | ({ kind: 'line' | 'rectangle' | 'ellipse' | 'arrow'; start: PaintPoint; end: PaintPoint } & PaintStyle)
  | { kind: 'text'; point: PaintPoint; text: string; color: string; size: number }

export interface PaintHistory {
  locked: readonly PaintCommand[]
  undoable: readonly PaintCommand[]
  redoable: readonly PaintCommand[]
  pointCount: number
}

export type HistoryCommitResult =
  | { ok: true; history: PaintHistory }
  | { ok: false; reason: 'command_limit' | 'point_limit' }

export function createPaintHistory(): PaintHistory {
  return { locked: [], undoable: [], redoable: [], pointCount: 0 }
}

export function commandPointCost(command: PaintCommand): number {
  if (command.kind === 'stroke') return command.points.length
  return command.kind === 'text' ? 1 : 2
}

export function allPaintCommands(history: PaintHistory): readonly PaintCommand[] {
  return [...history.locked, ...history.undoable]
}

export function commitPaintCommand(history: PaintHistory, command: PaintCommand): HistoryCommitResult {
  if (command.kind === 'stroke' && command.points.length > MAX_POINTS_PER_STROKE) {
    return { ok: false, reason: 'point_limit' }
  }
  const commandCount = history.locked.length + history.undoable.length
  if (commandCount >= MAX_COMMANDS) return { ok: false, reason: 'command_limit' }
  const pointCost = commandPointCost(command)
  if (history.pointCount + pointCost > MAX_TOTAL_POINTS) return { ok: false, reason: 'point_limit' }

  const nextUndoable = [...history.undoable, command]
  const shouldLock = nextUndoable.length > MAX_UNDO_COMMANDS
  return {
    ok: true,
    history: {
      locked: shouldLock ? [...history.locked, nextUndoable[0]!] : history.locked,
      undoable: shouldLock ? nextUndoable.slice(1) : nextUndoable,
      redoable: [],
      pointCount: history.pointCount + pointCost,
    },
  }
}

export function undoPaintCommand(history: PaintHistory): PaintHistory {
  const command = history.undoable.at(-1)
  if (!command) return history
  return {
    ...history,
    undoable: history.undoable.slice(0, -1),
    redoable: [command, ...history.redoable],
    pointCount: history.pointCount - commandPointCost(command),
  }
}

export function redoPaintCommand(history: PaintHistory): PaintHistory {
  const command = history.redoable[0]
  if (!command) return history
  return {
    ...history,
    undoable: [...history.undoable, command],
    redoable: history.redoable.slice(1),
    pointCount: history.pointCount + commandPointCost(command),
  }
}

export function validateCanvasSize(width: number, height: number): boolean {
  return Number.isInteger(width)
    && Number.isInteger(height)
    && width > 0
    && height > 0
    && width <= MAX_CANVAS_SIDE
    && height <= MAX_CANVAS_SIDE
    && width * height <= MAX_CANVAS_PIXELS
}

export function canvasPointFromClient(
  clientX: number,
  clientY: number,
  rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>,
  canvasWidth: number,
  canvasHeight: number,
): PaintPoint {
  if (rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 }
  return {
    x: clamp((clientX - rect.left) * canvasWidth / rect.width, 0, canvasWidth),
    y: clamp((clientY - rect.top) * canvasHeight / rect.height, 0, canvasHeight),
  }
}

export type AppendStrokePointResult =
  | { ok: true; points: readonly PaintPoint[] }
  | { ok: false; reason: 'point_limit' }

export function appendStrokePoint(
  points: readonly PaintPoint[],
  point: PaintPoint,
): AppendStrokePointResult {
  const previous = points.at(-1)
  if (previous && Math.hypot(point.x - previous.x, point.y - previous.y) < 0.75) {
    return { ok: true, points }
  }
  if (points.length >= MAX_POINTS_PER_STROKE) return { ok: false, reason: 'point_limit' }
  return { ok: true, points: [...points, point] }
}

export function renderPaintScene(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  background: string,
  commands: readonly PaintCommand[],
): void {
  context.save()
  context.setTransform(1, 0, 0, 1, 0, 0)
  context.clearRect(0, 0, width, height)
  context.fillStyle = background
  context.fillRect(0, 0, width, height)
  context.lineCap = 'round'
  context.lineJoin = 'round'
  for (const command of commands) renderPaintCommand(context, command, background)
  context.restore()
}

export function renderPaintCommand(
  context: CanvasRenderingContext2D,
  command: PaintCommand,
  background: string,
): void {
  context.save()
  context.strokeStyle = command.kind === 'stroke' && command.erase ? background : command.color
  context.fillStyle = command.kind === 'stroke' && command.erase ? background : command.color
  if (command.kind === 'text') {
    context.font = `${command.size}px system-ui, sans-serif`
    context.textBaseline = 'top'
    context.fillText(command.text, command.point.x, command.point.y)
    context.restore()
    return
  }

  context.lineWidth = command.width
  context.beginPath()
  if (command.kind === 'stroke') {
    const first = command.points[0]
    if (first) {
      context.moveTo(first.x, first.y)
      if (command.points.length === 1) context.lineTo(first.x + 0.01, first.y + 0.01)
      for (const point of command.points.slice(1)) context.lineTo(point.x, point.y)
    }
  } else if (command.kind === 'line') {
    context.moveTo(command.start.x, command.start.y)
    context.lineTo(command.end.x, command.end.y)
  } else if (command.kind === 'rectangle') {
    context.rect(command.start.x, command.start.y, command.end.x - command.start.x, command.end.y - command.start.y)
  } else if (command.kind === 'ellipse') {
    const centerX = (command.start.x + command.end.x) / 2
    const centerY = (command.start.y + command.end.y) / 2
    context.ellipse(
      centerX,
      centerY,
      Math.abs(command.end.x - command.start.x) / 2,
      Math.abs(command.end.y - command.start.y) / 2,
      0,
      0,
      Math.PI * 2,
    )
  } else {
    drawArrowPath(context, command.start, command.end, command.width)
  }
  context.stroke()
  context.restore()
}

function drawArrowPath(
  context: CanvasRenderingContext2D,
  start: PaintPoint,
  end: PaintPoint,
  width: number,
): void {
  const angle = Math.atan2(end.y - start.y, end.x - start.x)
  const head = Math.max(12, width * 4)
  context.moveTo(start.x, start.y)
  context.lineTo(end.x, end.y)
  context.moveTo(end.x, end.y)
  context.lineTo(end.x - head * Math.cos(angle - Math.PI / 6), end.y - head * Math.sin(angle - Math.PI / 6))
  context.moveTo(end.x, end.y)
  context.lineTo(end.x - head * Math.cos(angle + Math.PI / 6), end.y - head * Math.sin(angle + Math.PI / 6))
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}
